import { HTTPClientInterface, HTTPRequest } from "@shared/core/HTTPClientInterface";

import { buildURL, SystemURL } from "./URLCatalog";

export type SecondFactorAuthenticationPolicyMode = "all" | "selective";

export interface SecondFactorAuthenticationPolicy {
    mode: SecondFactorAuthenticationPolicyMode;
    subjectIDs: Record<string, boolean>;
    updatedAt?: number | string;
    updatedBy?: string;
}

export interface SecondFactorAuthenticationDevice {
    id: string;
    organizationID: string;
    userID: string;
    ticketID?: string;
    deviceInstallationID: string;
    devicePlatform?: string;
    appVersion?: string;
    status: string;
    appMetadata?: Record<string, any>;
    createdAt?: number | string;
    updatedAt?: number | string;
    deactivatedAt?: number | string;
    deactivatedBy?: string;
    deactivatedByName?: string;
}

export interface AuthAppInstallInvitationSummary {
    userID: string;
    status: "ENQUEUED" | "SKIPPED_ALREADY_INSTALLED";
    pendingMessageID?: string;
    expiresAt?: number;
}

export interface AuthAppInstallInvitationFailure {
    userID: string;
    error: string;
}

export interface AuthAppInstallInvitationsResult {
    enqueued: AuthAppInstallInvitationSummary[];
    skipped: AuthAppInstallInvitationSummary[];
    failed: AuthAppInstallInvitationFailure[];
}

export class SecondFactorAuthenticationService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getPolicy(orgID: string): Promise<SecondFactorAuthenticationPolicy> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.SecondFactorAuthentication.getPolicy, { org_id: orgID });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result as SecondFactorAuthenticationPolicy;
    }

    async updateMode(
        orgID: string,
        mode: SecondFactorAuthenticationPolicyMode,
    ): Promise<SecondFactorAuthenticationPolicy> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.SecondFactorAuthentication.putMode, { org_id: orgID });
        request.method = "PUT";
        request.body = { mode };
        const response = await this.httpClient.execute(request);
        return response.result as SecondFactorAuthenticationPolicy;
    }

    async updateSubjectOverride(
        orgID: string,
        subjectID: string,
        value: boolean,
    ): Promise<SecondFactorAuthenticationPolicy> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.SecondFactorAuthentication.putSubject, {
            org_id: orgID,
            subject_id: subjectID,
        });
        request.method = "PUT";
        request.body = { value };
        const response = await this.httpClient.execute(request);
        return response.result as SecondFactorAuthenticationPolicy;
    }

    async getDevices(orgID: string): Promise<SecondFactorAuthenticationDevice[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.SecondFactorAuthentication.getDevices, { org_id: orgID });
        request.method = "GET";
        const response = await this.httpClient.execute(request);
        return response.result as SecondFactorAuthenticationDevice[];
    }

    async deactivateDevice(
        orgID: string,
        deviceInstallationID: string,
    ): Promise<SecondFactorAuthenticationDevice> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.SecondFactorAuthentication.putDeactivateDevice, {
            org_id: orgID,
            device_installation_id: deviceInstallationID,
        });
        request.method = "PUT";
        const response = await this.httpClient.execute(request);
        return response.result as SecondFactorAuthenticationDevice;
    }

    async sendInstallInvitations(
        orgID: string,
        userIDs: string[],
    ): Promise<AuthAppInstallInvitationsResult> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.SecondFactorAuthentication.postInstallInvitations, {});
        request.method = "POST";
        request.body = { organizationID: orgID, userIDs };
        const response = await this.httpClient.execute(request);
        return response.result as AuthAppInstallInvitationsResult;
    }
}
